import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useTenant } from '../../context/TenantContext';
import { CreditCard, Save, ShieldCheck, Info, ExternalLink, Trash2, CheckCircle2, DollarSign, Users } from 'lucide-react';
import ConfirmModal from '../../components/ConfirmModal';

export default function PaymentSettings() {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('customer'); // 'customer' | 'payout'
    const [savingProvider, setSavingProvider] = useState(null); // 'cashfree' | 'razorpay' | 'cashfree_payout' | 'testMode'
    const [useTestPayment, setUseTestPayment] = useState(false);
    
    // Cashfree PG state
    const [cashfreeConfig, setCashfreeConfig] = useState({
        appId: '',
        secretKey: '',
        environment: 'sandbox',
        isActive: true
    });
    const [isCashfreeConfigured, setIsCashfreeConfigured] = useState(false);

    // Razorpay state
    const [razorpayConfig, setRazorpayConfig] = useState({
        keyId: '',
        keySecret: '',
        environment: 'sandbox',
        isActive: true
    });
    const [isRazorpayConfigured, setIsRazorpayConfigured] = useState(false);

    // Cashfree Payout state
    const [payoutConfig, setPayoutConfig] = useState({
        appId: '',
        secretKey: '',
        environment: 'sandbox',
        isActive: true
    });
    const [isPayoutConfigured, setIsPayoutConfigured] = useState(false);

    // Delete modal target
    const [deleteTarget, setDeleteTarget] = useState(null); // 'cashfree' | 'razorpay' | 'cashfree_payout'

    const toast = useToast();
    const { refreshTenant } = useTenant();

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await api.get('/api/restaurants/settings/payment');
            const data = res.data;
            setUseTestPayment(data.useTestPayment || false);

            const cf = (data.configs || []).find(c => c.provider === 'cashfree');
            if (cf) {
                setCashfreeConfig({
                    appId: cf.config.appId || '',
                    secretKey: cf.config.secretKey || '',
                    environment: cf.environment || 'sandbox',
                    isActive: cf.is_active
                });
                setIsCashfreeConfigured(true);
            } else {
                setIsCashfreeConfigured(false);
            }

            const rzp = (data.configs || []).find(c => c.provider === 'razorpay');
            if (rzp) {
                setRazorpayConfig({
                    keyId: rzp.config.keyId || rzp.config.appId || '',
                    keySecret: rzp.config.keySecret || rzp.config.secretKey || '',
                    environment: rzp.environment || 'sandbox',
                    isActive: rzp.is_active
                });
                setIsRazorpayConfigured(true);
            } else {
                setIsRazorpayConfigured(false);
            }

            const payout = (data.configs || []).find(c => c.provider === 'cashfree_payout');
            if (payout) {
                setPayoutConfig({
                    appId: payout.config.appId || '',
                    secretKey: payout.config.secretKey || '',
                    environment: payout.environment || 'sandbox',
                    isActive: payout.is_active
                });
                setIsPayoutConfigured(true);
            } else {
                setIsPayoutConfigured(false);
            }
        } catch (err) {
            toast.error('Failed to fetch payment settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGateway = async (e, provider) => {
        e.preventDefault();
        setSavingProvider(provider);
        try {
            let configPayload;
            let envPayload;
            let activePayload;

            if (provider === 'razorpay') {
                configPayload = { keyId: razorpayConfig.keyId, keySecret: razorpayConfig.keySecret };
                envPayload = razorpayConfig.environment;
                activePayload = razorpayConfig.isActive;
            } else if (provider === 'cashfree') {
                configPayload = { appId: cashfreeConfig.appId, secretKey: cashfreeConfig.secretKey };
                envPayload = cashfreeConfig.environment;
                activePayload = cashfreeConfig.isActive;
            } else if (provider === 'cashfree_payout') {
                configPayload = { appId: payoutConfig.appId, secretKey: payoutConfig.secretKey };
                envPayload = payoutConfig.environment;
                activePayload = payoutConfig.isActive;
            }

            await api.post('/api/restaurants/settings/payment', {
                provider,
                config: configPayload,
                environment: envPayload,
                is_active: activePayload
            });

            if (provider === 'razorpay') {
                setIsRazorpayConfigured(true);
            } else if (provider === 'cashfree') {
                setIsCashfreeConfigured(true);
            } else if (provider === 'cashfree_payout') {
                setIsPayoutConfigured(true);
            }

            toast.success(`${provider === 'razorpay' ? 'Razorpay' : provider === 'cashfree' ? 'Cashfree' : 'Cashfree Payout'} settings verified & saved successfully!`);
            window.dispatchEvent(new Event('paymentConfigUpdated'));
        } catch (err) {
            toast.error(err.response?.data?.message || `Failed to update ${provider} settings`);
        } finally {
            setSavingProvider(null);
        }
    };

    const handleDeleteGateway = async () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        setSavingProvider(target);
        try {
            await api.delete(`/api/restaurants/settings/payment/${target}`);
            if (target === 'cashfree') {
                setCashfreeConfig({ appId: '', secretKey: '', environment: 'sandbox', isActive: true });
                setIsCashfreeConfigured(false);
            } else if (target === 'razorpay') {
                setRazorpayConfig({ keyId: '', keySecret: '', environment: 'sandbox', isActive: true });
                setIsRazorpayConfigured(false);
            } else if (target === 'cashfree_payout') {
                setPayoutConfig({ appId: '', secretKey: '', environment: 'sandbox', isActive: true });
                setIsPayoutConfigured(false);
            }
            toast.success(`${target === 'razorpay' ? 'Razorpay' : target === 'cashfree' ? 'Cashfree' : 'Cashfree Payout'} configuration deleted`);
            window.dispatchEvent(new Event('paymentConfigUpdated'));
        } catch (err) {
            toast.error(`Failed to delete ${target} configuration`);
        } finally {
            setSavingProvider(null);
        }
    };

    const handleUpdateStatus = async (provider, newStatus) => {
        setSavingProvider(provider);
        try {
            let configPayload;
            let envPayload;

            if (provider === 'razorpay') {
                configPayload = { keyId: razorpayConfig.keyId, keySecret: razorpayConfig.keySecret };
                envPayload = razorpayConfig.environment;
            } else if (provider === 'cashfree') {
                configPayload = { appId: cashfreeConfig.appId, secretKey: cashfreeConfig.secretKey };
                envPayload = cashfreeConfig.environment;
            } else if (provider === 'cashfree_payout') {
                configPayload = { appId: payoutConfig.appId, secretKey: payoutConfig.secretKey };
                envPayload = payoutConfig.environment;
            }

            await api.post('/api/restaurants/settings/payment', {
                provider,
                config: configPayload,
                environment: envPayload,
                is_active: newStatus
            });

            if (provider === 'razorpay') {
                setRazorpayConfig(prev => ({ ...prev, isActive: newStatus }));
            } else if (provider === 'cashfree') {
                setCashfreeConfig(prev => ({ ...prev, isActive: newStatus }));
            } else if (provider === 'cashfree_payout') {
                setPayoutConfig(prev => ({ ...prev, isActive: newStatus }));
            }

            toast.success(`${provider === 'razorpay' ? 'Razorpay' : provider === 'cashfree' ? 'Cashfree' : 'Cashfree Payout'} gateway ${newStatus ? 'enabled' : 'disabled'}`);
            window.dispatchEvent(new Event('paymentConfigUpdated'));
        } catch (err) {
            toast.error('Failed to update status');
        } finally {
            setSavingProvider(null);
        }
    };

    const handleToggleTestMode = async (newVal) => {
        setSavingProvider('testMode');
        try {
            await api.post('/api/restaurants/settings/payment/toggle-test-mode', {
                use_test_payment: newVal
            });
            setUseTestPayment(newVal);
            toast.success(`Aaharam Cloud Test Mode ${newVal ? 'enabled' : 'disabled'}`);
            refreshTenant();
            window.dispatchEvent(new Event('paymentConfigUpdated'));
        } catch (err) {
            toast.error('Failed to update test mode');
        } finally {
            setSavingProvider(null);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
    );

    const testModeCard = (
        <div key="test-mode" className={`rounded-2xl overflow-hidden border-2 transition-all duration-500 ${useTestPayment ? 'bg-indigo-50 border-indigo-200 shadow-md' : 'bg-white border-gray-100 hover:border-gray-200'}`}>
            <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-start space-x-4">
                        <div className={`p-3 rounded-xl transition-colors duration-500 ${useTestPayment ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                            <CreditCard className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                Aaharam Cloud Test Mode
                                {useTestPayment && (
                                    <span className="ml-3 px-2 py-0.5 bg-indigo-600 text-white text-[10px] uppercase tracking-widest rounded-full animate-pulse">Active</span>
                                )}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1 max-w-md">
                                When enabled, your restaurant will use Aaharam's platform-level sandbox keys. 
                                Payments will be in <span className="font-bold text-indigo-600 uppercase tracking-wider">Test Mode</span>.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm self-start md:self-center">
                        <button
                            type="button"
                            onClick={() => handleToggleTestMode(!useTestPayment)}
                            disabled={!!savingProvider}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${useTestPayment ? 'bg-indigo-600' : 'bg-gray-200'}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useTestPayment ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className="text-xs font-black uppercase tracking-widest text-gray-600 w-20">{useTestPayment ? 'Enabled' : 'Disabled'}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const cashfreeGatewaySection = (
        <div key="cashfree-section" className={`bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm transition-all duration-500 ${useTestPayment ? 'opacity-40 grayscale pointer-events-none select-none relative' : ''}`}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center p-2">
                        <img src="https://cdn.brandfetch.io/cashfree.com/fallback/lettermark/theme/dark/h/256/w/256/icon?c=1bfwsmEH20zzEfSNTed" alt="Cashfree" className="max-h-full max-w-full object-contain" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            Cashfree Payments
                            {isCashfreeConfigured && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        </h2>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-primary-600">UPI, NetBanking, Cards & Wallets</span>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        type="button"
                        onClick={() => isCashfreeConfigured && handleUpdateStatus('cashfree', !cashfreeConfig.isActive)}
                        disabled={!isCashfreeConfigured || !!savingProvider}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${cashfreeConfig.isActive ? 'bg-green-500' : 'bg-gray-300'} ${!isCashfreeConfigured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${cashfreeConfig.isActive ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-tight w-12">{cashfreeConfig.isActive ? 'Active' : 'Inactive'}</span>
                </div>
            </div>

            <form onSubmit={(e) => handleSaveGateway(e, 'cashfree')} className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700 flex items-center">
                            App ID (Incoming Gateway)
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={cashfreeConfig.appId}
                            onChange={(e) => setCashfreeConfig({ ...cashfreeConfig, appId: e.target.value })}
                            disabled={isCashfreeConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500 font-mono"
                            placeholder="Enter Cashfree App ID"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700 flex items-center">
                            Secret Key
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="password"
                            required
                            value={cashfreeConfig.secretKey}
                            onChange={(e) => setCashfreeConfig({ ...cashfreeConfig, secretKey: e.target.value })}
                            disabled={isCashfreeConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500 font-mono"
                            placeholder="Enter Cashfree Secret Key"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700">Environment</label>
                        <select
                            value={cashfreeConfig.environment}
                            onChange={(e) => setCashfreeConfig({ ...cashfreeConfig, environment: e.target.value })}
                            disabled={isCashfreeConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        >
                            <option value="sandbox">Sandbox (Testing)</option>
                            <option value="PRODUCTION">Production (Live)</option>
                        </select>
                    </div>

                    <div className="space-y-1.5 flex flex-col justify-end">
                        <label className={`flex items-center cursor-pointer space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200 ${savingProvider ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                                type="checkbox"
                                checked={cashfreeConfig.isActive}
                                onChange={(e) => {
                                    const newVal = e.target.checked;
                                    if (isCashfreeConfigured) {
                                        handleUpdateStatus('cashfree', newVal);
                                    } else {
                                        setCashfreeConfig({ ...cashfreeConfig, isActive: newVal });
                                    }
                                }}
                                disabled={!!savingProvider}
                                className="w-4 h-4 text-primary-600 rounded disabled:opacity-50"
                            />
                            <span className="text-sm font-bold text-gray-700">Enable this gateway</span>
                        </label>
                    </div>
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <a
                        href="https://www.cashfree.com/payment-gateway-india/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-primary-600 flex items-center hover:underline"
                    >
                        Get credentials from Cashfree Dashboard
                        <ExternalLink className="w-3 h-3 ml-1" />
                    </a>

                    <div className="flex items-center space-x-3">
                        {isCashfreeConfigured && (
                            <button
                                type="button"
                                onClick={() => setDeleteTarget('cashfree')}
                                disabled={!!savingProvider}
                                className="flex items-center space-x-2 bg-red-50 text-red-600 px-6 py-3 rounded-xl font-bold hover:bg-red-100 transition-all disabled:opacity-50"
                            >
                                <Trash2 className="w-5 h-5" />
                                <span>Delete</span>
                            </button>
                        )}

                        {!isCashfreeConfigured && (
                            <button
                                type="submit"
                                disabled={!!savingProvider}
                                className="flex items-center space-x-2 bg-gray-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-gray-800 transition-all disabled:opacity-50"
                            >
                                {savingProvider === 'cashfree' ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-b-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        <span>Verify & Save</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );

    const razorpayGatewaySection = (
        <div key="razorpay-section" className={`bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm transition-all duration-500 ${useTestPayment ? 'opacity-40 grayscale pointer-events-none select-none relative' : ''}`}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-900 rounded-xl shadow-sm border border-blue-800 flex items-center justify-center p-2">
                        <span className="text-white font-black text-xs tracking-tighter">RAZORPAY</span>
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            Razorpay Payments
                            {isRazorpayConfigured && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        </h2>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-blue-600">Instant UPI, Credit/Debit, Wallets & NetBanking</span>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        type="button"
                        onClick={() => isRazorpayConfigured && handleUpdateStatus('razorpay', !razorpayConfig.isActive)}
                        disabled={!isRazorpayConfigured || !!savingProvider}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${razorpayConfig.isActive ? 'bg-green-500' : 'bg-gray-300'} ${!isRazorpayConfigured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${razorpayConfig.isActive ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-tight w-12">{razorpayConfig.isActive ? 'Active' : 'Inactive'}</span>
                </div>
            </div>

            <form onSubmit={(e) => handleSaveGateway(e, 'razorpay')} className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700 flex items-center">
                            Razorpay Key ID
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={razorpayConfig.keyId}
                            onChange={(e) => setRazorpayConfig({ ...razorpayConfig, keyId: e.target.value })}
                            disabled={isRazorpayConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500 font-mono"
                            placeholder="e.g. rzp_test_xxxxxx or rzp_live_xxxxxx"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700 flex items-center">
                            Razorpay Key Secret
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="password"
                            required
                            value={razorpayConfig.keySecret}
                            onChange={(e) => setRazorpayConfig({ ...razorpayConfig, keySecret: e.target.value })}
                            disabled={isRazorpayConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500 font-mono"
                            placeholder="Enter Razorpay Key Secret"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700">Environment</label>
                        <select
                            value={razorpayConfig.environment}
                            onChange={(e) => setRazorpayConfig({ ...razorpayConfig, environment: e.target.value })}
                            disabled={isRazorpayConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        >
                            <option value="sandbox">Test Mode (Sandbox)</option>
                            <option value="PRODUCTION">Live Mode (Production)</option>
                        </select>
                    </div>

                    <div className="space-y-1.5 flex flex-col justify-end">
                        <label className={`flex items-center cursor-pointer space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200 ${savingProvider ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                                type="checkbox"
                                checked={razorpayConfig.isActive}
                                onChange={(e) => {
                                    const newVal = e.target.checked;
                                    if (isRazorpayConfigured) {
                                        handleUpdateStatus('razorpay', newVal);
                                    } else {
                                        setRazorpayConfig({ ...razorpayConfig, isActive: newVal });
                                    }
                                }}
                                disabled={!!savingProvider}
                                className="w-4 h-4 text-blue-600 rounded disabled:opacity-50"
                            />
                            <span className="text-sm font-bold text-gray-700">Enable this gateway</span>
                        </label>
                    </div>
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <a
                        href="https://dashboard.razorpay.com/app/keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-blue-600 flex items-center hover:underline"
                    >
                        Get API keys from Razorpay Dashboard
                        <ExternalLink className="w-3 h-3 ml-1" />
                    </a>

                    <div className="flex items-center space-x-3">
                        {isRazorpayConfigured && (
                            <button
                                type="button"
                                onClick={() => setDeleteTarget('razorpay')}
                                disabled={!!savingProvider}
                                className="flex items-center space-x-2 bg-red-50 text-red-600 px-6 py-3 rounded-xl font-bold hover:bg-red-100 transition-all disabled:opacity-50"
                            >
                                <Trash2 className="w-5 h-5" />
                                <span>Delete</span>
                            </button>
                        )}

                        {!isRazorpayConfigured && (
                            <button
                                type="submit"
                                disabled={!!savingProvider}
                                className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all disabled:opacity-50 shadow-md shadow-blue-200"
                            >
                                {savingProvider === 'razorpay' ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-b-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        <span>Verify & Save</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );

    const cashfreePayoutGatewaySection = (
        <div key="cashfree-payout-section" className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center p-2 bg-indigo-50">
                        <DollarSign className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            Cashfree Direct Payouts
                            {isPayoutConfigured && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        </h2>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-600">Direct Staff/Delivery Bank Payout Transact</span>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        type="button"
                        onClick={() => isPayoutConfigured && handleUpdateStatus('cashfree_payout', !payoutConfig.isActive)}
                        disabled={!isPayoutConfigured || !!savingProvider}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${payoutConfig.isActive ? 'bg-green-500' : 'bg-gray-300'} ${!isPayoutConfigured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${payoutConfig.isActive ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-tight w-12">{payoutConfig.isActive ? 'Active' : 'Inactive'}</span>
                </div>
            </div>

            <form onSubmit={(e) => handleSaveGateway(e, 'cashfree_payout')} className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700 flex items-center">
                            Payout Client ID
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={payoutConfig.appId}
                            onChange={(e) => setPayoutConfig({ ...payoutConfig, appId: e.target.value })}
                            disabled={isPayoutConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500 font-mono"
                            placeholder="Enter Cashfree Payout Client ID"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700 flex items-center">
                            Payout Client Secret
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            type="password"
                            required
                            value={payoutConfig.secretKey}
                            onChange={(e) => setPayoutConfig({ ...payoutConfig, secretKey: e.target.value })}
                            disabled={isPayoutConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500 font-mono"
                            placeholder="Enter Cashfree Payout Client Secret"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700">Environment</label>
                        <select
                            value={payoutConfig.environment}
                            onChange={(e) => setPayoutConfig({ ...payoutConfig, environment: e.target.value })}
                            disabled={isPayoutConfigured}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all outline-none text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        >
                            <option value="sandbox">Sandbox (Testing)</option>
                            <option value="PRODUCTION">Production (Live)</option>
                        </select>
                    </div>

                    <div className="space-y-1.5 flex flex-col justify-end">
                        <label className={`flex items-center cursor-pointer space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200 ${savingProvider ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                                type="checkbox"
                                checked={payoutConfig.isActive}
                                onChange={(e) => {
                                    const newVal = e.target.checked;
                                    if (isPayoutConfigured) {
                                        handleUpdateStatus('cashfree_payout', newVal);
                                    } else {
                                        setPayoutConfig({ ...payoutConfig, isActive: newVal });
                                    }
                                }}
                                disabled={!!savingProvider}
                                className="w-4 h-4 text-indigo-600 rounded disabled:opacity-50"
                            />
                            <span className="text-sm font-bold text-gray-700">Enable Payout service</span>
                        </label>
                    </div>
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <a
                        href="https://dashboard.cashfree.com/payouts"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-indigo-600 flex items-center hover:underline"
                    >
                        Get credentials from Cashfree Payout Dashboard
                        <ExternalLink className="w-3 h-3 ml-1" />
                    </a>

                    <div className="flex items-center space-x-3">
                        {isPayoutConfigured && (
                            <button
                                type="button"
                                onClick={() => setDeleteTarget('cashfree_payout')}
                                disabled={!!savingProvider}
                                className="flex items-center space-x-2 bg-red-50 text-red-600 px-6 py-3 rounded-xl font-bold hover:bg-red-100 transition-all disabled:opacity-50"
                            >
                                <Trash2 className="w-5 h-5" />
                                <span>Delete</span>
                            </button>
                        )}

                        {!isPayoutConfigured && (
                            <button
                                type="submit"
                                disabled={!!savingProvider}
                                className="flex items-center space-x-2 bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 shadow-md shadow-indigo-200"
                            >
                                {savingProvider === 'cashfree_payout' ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-b-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        <span>Verify & Save</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );

    const infoAlert = (
        <div key="info-alert" className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-700">
                <p className="font-semibold">Professional Multi-Tenant Payments & Payouts</p>
                <p className="mt-1 text-xs">
                    By connecting your own configurations, incoming order funds land directly in your merchant account. 
                    Outgoing staff salary disbursements debit automatically from your Cashfree Payouts balance.
                </p>
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-12">
            {/* Header tab switcher */}
            <div className="flex flex-col md:flex-row md:items-center justify-between pb-5 border-b border-gray-200 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Payment Settings</h1>
                    <p className="text-gray-500 mt-1 text-xs">Configure payment options for customer orders and staff payroll disbursements.</p>
                </div>
                <div className="flex bg-gray-100 p-1 rounded-xl self-start md:self-center">
                    <button
                        onClick={() => setActiveTab('customer')}
                        className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                            activeTab === 'customer' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-950'
                        }`}
                    >
                        <CreditCard className="w-4 h-4" /> Customer Gateway
                    </button>
                    <button
                        onClick={() => setActiveTab('payout')}
                        className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                            activeTab === 'payout' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-950'
                        }`}
                    >
                        <Users className="w-4 h-4" /> Staff Payouts
                    </button>
                </div>
            </div>

            {infoAlert}

            {activeTab === 'customer' && (
                <div className="space-y-6">
                    {(() => {
                        const items = [];
                        // Push configured gateways first
                        if (isRazorpayConfigured) {
                            items.push(razorpayGatewaySection);
                        }
                        if (isCashfreeConfigured) {
                            items.push(cashfreeGatewaySection);
                        }
                        // Push unconfigured gateways next
                        if (!isRazorpayConfigured) {
                            items.push(razorpayGatewaySection);
                        }
                        if (!isCashfreeConfigured) {
                            items.push(cashfreeGatewaySection);
                        }
                        // Deduplicate by JSX key using reference sets
                        const orderedComponents = Array.from(new Set(items));

                        return useTestPayment 
                            ? [testModeCard, ...orderedComponents]
                            : [...orderedComponents, testModeCard];
                    })()}
                </div>
            )}

            {activeTab === 'payout' && (
                <div className="space-y-6">
                    {cashfreePayoutGatewaySection}
                </div>
            )}

            <ConfirmModal
                isOpen={!!deleteTarget}
                title={`Delete ${deleteTarget === 'razorpay' ? 'Razorpay' : deleteTarget === 'cashfree' ? 'Cashfree' : 'Cashfree Payout'} Configuration`}
                message={`Are you sure you want to delete your config? Associated transaction actions will be disabled.`}
                confirmText="Delete Now"
                onConfirm={handleDeleteGateway}
                onCancel={() => setDeleteTarget(null)}
                variant="danger"
            />
        </div>
    );
}
